import React, { useState, useEffect } from "react";
import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Pagination,
  MenuItem,
  FormControl,
  Select,
  Grid,
  Stack,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import Skeleton from "@mui/material/Skeleton";
import ErrorPage from "../Components/ErrorPage";
import ShopAdd from "./ShopAdd";
import ShopEdit from "./ShopEdit";
import useDelete from "../../api/useDelete";
import { useTranslation } from "react-i18next";
import { useTenant } from "../../context/TenantContext";

const ShopIndex = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const currentUser = auth?.user?.user || auth?.user;
  const isSuperAdmin = Boolean(currentUser?.is_superuser);
  const { shop: currentShop, isLoading: isTenantLoading } = useTenant();
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState(null);

  // Fetch shops with search filtering and pagination
  const { data, isLoading, isError, refetch } = useFetchObjects(
    ["shop", searchTerm, page, rowsPerPage],
    `shop/shop/?search=${searchTerm}&page=${page}&page_size=${rowsPerPage}`,
    token
  );

  useEffect(() => {
    refetch();
  }, [searchTerm, page, rowsPerPage, refetch]);

  const { handleDelete, ConfirmDialog } = useDelete("shop/shop", token);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setPage(1);
  };

  const shops = data && data.results ? data.results : data || [];
  const totalCount = data && data.count ? data.count : shops.length;
  const hasExistingShop = Boolean(currentShop) || shops.length > 0;
  const displayedShop = currentShop || shops[0] || null;

  if (isError) {
    return <ErrorPage message={t("shop.failedToLoadShops")} />;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          mb: 2,
          gap: 2,
        }}
      >
        <Typography variant="h4">{t("shop.shopList")}</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpenAdd(true)}
        >
          {isSuperAdmin
            ? t("shop.addNewShop", "Add New Shop")
            : hasExistingShop
            ? t("shop.viewCreateHelp", "Create is limited to one shop per user")
            : t("shop.addNewShop")}
        </Button>
      </Box>
      <Typography variant="subtitle1" color="textSecondary" mb={2}>
        {t("shop.dataLoadedOn")} {new Date().toLocaleString()}
      </Typography>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          {isTenantLoading ? (
            <Skeleton variant="rectangular" height={120} />
          ) : displayedShop && !isSuperAdmin ? (
            <Stack spacing={1}>
              <Typography variant="h5">{displayedShop.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t("shop.shopId", "Shop ID")}: {displayedShop.shop_id || `SHOP-${displayedShop.id}`}
              </Typography>
              <Typography variant="body1">
                {displayedShop.description || t("shop.noDescription", "No description added yet.")}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, pt: 1 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setEditData(displayedShop);
                    setOpenEdit(true);
                  }}
                >
                  {t("shop.editShop", "Edit Shop")}
                </Button>
              </Box>
            </Stack>
          ) : (
            <Stack >
             
            </Stack>
          )}
        </CardContent>
      </Card>
      {/* Search Box */}
      <Box sx={{ mb: 2 }}>
        <TextField
          variant="outlined"
          fullWidth
          placeholder={t("shop.searchShops")}
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      {/* Shop Table */}
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table sx={{ minWidth: 650 }} aria-label="shop table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              <TableCell sx={{ color: "white" }}>{t("shop.id")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("shop.name")}</TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("shop.description")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>
                {t("shop.createdAt")}
              </TableCell>
              <TableCell sx={{ color: "white" }}>{t("shop.action")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from(new Array(5)).map((_, rowIndex) => (
                  <TableRow key={`skeleton-${rowIndex}`}>
                    {["ID", "Name", "Description", "Created At", "Action"].map(
                      (_, colIndex) => (
                        <TableCell key={`skeleton-${rowIndex}-${colIndex}`}>
                          <Skeleton variant="text" />
                        </TableCell>
                      )
                    )}
                  </TableRow>
                ))
              : shops.map((shop) => (
                  <TableRow key={shop.id} hover>
                    <TableCell>{(page - 1) * rowsPerPage + shops.indexOf(shop) + 1}</TableCell>
                    <TableCell>{shop.name}</TableCell>
                    <TableCell>{shop.description}</TableCell>
                    <TableCell>
                      {shop.created_at
                        ? new Date(shop.created_at).toLocaleString()
                        : "N/A"}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={() => {
                          setEditData(shop);
                          setOpenEdit(true);
                        }}
                      >
                        {t("shop.edit")}
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleDelete(shop.id)}
                      >
                        {t("shop.delete")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        padding="10px"
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography>{t("shop.rowsPerPage")}</Typography>
          <FormControl
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  border: "none",
                },
              },
            }}
            size="small"
          >
            <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Pagination
          count={Math.ceil(totalCount / rowsPerPage)}
          page={page}
          onChange={handlePageChange}
          variant="outlined"
          color="primary"
          sx={{ ml: 2 }}
        />
      </Grid>

      {openAdd && (
        <ShopAdd
          open={openAdd}
          onClose={() => setOpenAdd(false)}
          token={token}
        />
      )}
      {openEdit && (
        <ShopEdit
          open={openEdit}
          onClose={() => setOpenEdit(false)}
          editData={editData}
          token={token}
          owner={auth?.user?.user?.id}
        />
      )}
      <ConfirmDialog />
    </Box>
  );
};

export default ShopIndex;
